import React from 'react';
import { TableCell, Box } from '@mui/material';
import _ from 'lodash';
import {
  prjSalesHeaderTyps,
  prjTargetRateProfitHeaderTyps,
  prjRealSalesProfitHeaderTyps,
  prjStatProfitHeaderTyps,
} from '../../utils/forecastStaticData';
import { getNumberComma } from '../../utils/numberUtils';

const ProfitStaticTableCell = ({ headerRows, data, isTotalRow, selectPrjTyp, useKorean = true, onCellClick, clickOn = false }) => {
  if (!headerRows || !data) return null;

  const formatKoreanUnit = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num === 0) return { upper: '0', lower: '' };
    const absNum = Math.abs(Math.floor(num));
    const sign = num < 0 ? '-' : '';
    const eok = Math.floor(absNum / 100000000); 
    const man = Math.floor((absNum % 100000000) / 10000); 
    const rest = absNum % 10000; 
    let upper = '', lower = '';
    if (eok > 0) {
      upper = `${sign}${getNumberComma(eok)}억`;
      const manPart = man > 0 ? `${getNumberComma(man)}만 ` : '';
      const restPart = rest > 0 ? `${getNumberComma(rest)}` : '';
      lower = (manPart + restPart).trim();
    } else if (man > 0) {
      upper = `${sign}${getNumberComma(man)}만`;
      lower = rest > 0 ? getNumberComma(rest) : '';
    } else {
      upper = `${sign}${getNumberComma(rest)}`;
      lower = '';
    }
    return { upper, lower };
  };

  return headerRows.map((field, fIndex) => {
    if (field.id?.includes('STAT') && selectPrjTyp !== 'A') return null;

    const isSingleRowHeader = field.id === 'SELLINGEX' || field.id === 'BUDGETAMT';

    const headerTyp =
      field?.id === 'STATRATE' ? prjTargetRateProfitHeaderTyps :
      field?.id?.includes('STAT') ? prjStatProfitHeaderTyps :  
      field?.id === 'OA' ? prjRealSalesProfitHeaderTyps : 
      isSingleRowHeader ? [{ id: field.id, name: field.name }] : prjSalesHeaderTyps;

    return headerTyp.map((type, tIndex) => {
      if (type.id?.includes('STAT') && selectPrjTyp !== 'A') return null;

      const key = `${fIndex}_${tIndex}`;
      
      let dataKey = type.id;
      if (field.id === 'SELLINGEX') dataKey = 'sellingEx';
      if (field.id === 'BUDGETAMT') dataKey = 'budgetAmt';

      let value = _.get(data, isSingleRowHeader ? `OA.${dataKey}` : `${field.val}.${type.id}`, '-');
      let fontColor = '#202124'; 
      let fontWeight = isTotalRow ? 600 : 500; 
      
      // % 포함 여부 확인
      let isPercentage = type.id === 'salesProfitMargin' || type.id?.toLowerCase().includes('rate') || type.name?.includes('%');
      
      const isPureHyphen = value === '-' || value === null || value === undefined;
      const numValue = parseFloat(value);
      const isNegative = !isPureHyphen && !isNaN(numValue) && numValue < 0; 
      const isZero = !isPureHyphen && !isNaN(numValue) && numValue === 0;

      const isClickable = clickOn && (type.id === 'sales' || type.id === 'purchase' || type.id === 'collaborationBenefits' || type.id === 'collaborationCost') && !isTotalRow;

      let displayContent;
      if (isPercentage) {
        if (isNaN(numValue) || numValue === 0) {
          displayContent = '0%';
          fontColor = '#70757a'; 
          fontWeight = 400;
        } else {
          displayContent = `${numValue.toFixed(0)}%`;
          fontColor = numValue > 0 ? '#1967d2' : '#d93025';
          fontWeight = 500;
        }
      } else if (isPureHyphen) {
        displayContent = '-';
        fontColor = '#1a73e8'; 
        fontWeight = 700;
      } else if (isZero) {
        displayContent = '0';
        fontColor = '#80868b';
        fontWeight = 400;
      } else {
        if (isNegative) fontColor = '#d93025';

        if (useKorean) {
          const { upper, lower } = formatKoreanUnit(value);
          displayContent = (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
              <span style={{ 
                fontSize: '0.85rem', fontWeight, 
                color: isClickable ? '#1a73e8' : (field.id === 'ES' && !isNegative ? '#311b92' : fontColor),
                textDecoration: isClickable ? 'underline' : 'none'
              }}>{upper}</span>
              {lower && <span style={{ fontSize: '0.75rem', color: '#989999', marginTop: '1px' }}>{lower}</span>}
            </Box>
          );
        } else {
          displayContent = getNumberComma(value);
        }
        
        if (field.id === 'ES' && !isNegative) {
          fontColor = type.id === 'revenue' ? '#1a237e' : '#311b92'; 
          fontWeight = 600;
        }
      }

      // 테이블 헤더와 동일하게 너비 설정 (퍼센트: 70px, 일반: 100px)
      const colWidth = isPercentage ? '70px' : '100px';

      return (
        <TableCell
          key={key}
          align={isPercentage ? "center" : "right"} 
          onClick={() => isClickable && onCellClick && onCellClick(data, field, type)}
          sx={{
            width: colWidth,
            minWidth: colWidth,
            padding: isPercentage ? '6px 2px' : '6px 4px', // 너비 좁은 곳은 패딩 축소
            whiteSpace: 'nowrap',
            bgcolor: isTotalRow ? (data.id === 'one' ? '#f5f5f5' : '#edf2f7') : 'transparent', 
            borderRight: tIndex === headerTyp.length - 1 ? '1px solid #ddd' : '1px solid #f1f3f4',
            cursor: isClickable ? 'pointer' : 'default',
          }}
        >
          {typeof displayContent === 'string' ? (
            <span style={{ 
              fontSize: '0.8rem', fontWeight, color: fontColor,
              display: isPercentage ? 'inline-block' : 'inline',
              width: isPercentage ? '100%' : 'auto',
              textAlign: isPercentage ? 'center' : 'inherit',
              textDecoration: isClickable ? 'underline' : 'none'
            }}>{displayContent}</span>
          ) : displayContent}
        </TableCell>
      );
    });
  });
};

export default ProfitStaticTableCell;
